import React, {useCallback, useEffect, useState} from 'react';
import {useRecoilState} from 'recoil';
import {loadingAtom} from '../../atoms';
import {getFreightConfiguration, saveFreightConfiguration} from '../../utils/requestUtils';
import DecimalInput from '../common/DecimalInput';
import DeleteButton from '../common/DeleteButton';
import './CompanyClosedDaysCalendar.css';

const MODE_PER_KM = 'PER_KM';
const MODE_KM_RANGE = 'KM_RANGE';

const newKmRange = () => ({maxKm: '', value: 0});

const FreightConfiguration = ({disabled = false}) => {
    const [, setLoading] = useRecoilState(loadingAtom);
    const [calculationMode, setCalculationMode] = useState(MODE_PER_KM);
    const [fixedValue, setFixedValue] = useState(0);
    const [perKmValue, setPerKmValue] = useState(0);
    const [kmRanges, setKmRanges] = useState([newKmRange()]);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isDirty, setIsDirty] = useState(false);

    const resetFeedback = () => {
        setErrorMessage('');
        setSuccessMessage('');
    };

    const loadConfiguration = useCallback(async () => {
        resetFeedback();
        setLoading(true);
        try {
            const response = await getFreightConfiguration();
            setCalculationMode(response?.calculationMode === MODE_KM_RANGE ? MODE_KM_RANGE : MODE_PER_KM);
            setFixedValue(response?.fixedValue ? response.fixedValue : 0);
            setPerKmValue(response?.perKmValue ? response.perKmValue : 0);
            setKmRanges(Array.isArray(response?.kmRanges) && response.kmRanges.length > 0
                ? response.kmRanges.map((r) => ({maxKm: r.maxKm || '', value: r.value || 0}))
                : [newKmRange()]);
            setIsDirty(false);
        } catch (error) {
            console.error('Erro ao carregar configuração de frete:', error);
            setErrorMessage('Não foi possível carregar a configuração de frete.');
        } finally {
            setLoading(false);
        }
    }, [setLoading]);

    useEffect(() => {
        loadConfiguration();
    }, [loadConfiguration]);

    // Mantém sempre uma linha vazia ao final da tabela: assim que a última faixa
    // recebe uma quilometragem, uma nova linha em branco é adicionada
    // (mesmo padrão de Peças/Quantidade da tela de cadastro de produtos).
    useEffect(() => {
        const last = kmRanges[kmRanges.length - 1];
        if (kmRanges.length === 0 || (last && last.maxKm !== '' && Number(last.maxKm) > 0)) {
            setKmRanges((prev) => [...prev, newKmRange()]);
        }
    }, [kmRanges]);

    const handleModeChange = (mode) => {
        if (disabled) {
            return;
        }
        resetFeedback();
        setCalculationMode(mode);
        setIsDirty(true);
    };

    const handleFixedValueChange = (e) => {
        resetFeedback();
        setFixedValue(parseFloat(e.target.value) || 0);
        setIsDirty(true);
    };

    const handlePerKmValueChange = (e) => {
        resetFeedback();
        setPerKmValue(parseFloat(e.target.value) || 0);
        setIsDirty(true);
    };

    const handleKmRangeChange = (index, field, value) => {
        resetFeedback();
        setKmRanges((prev) => prev.map((range, i) =>
            i === index ? {...range, [field]: value} : range));
        setIsDirty(true);
    };

    const removeKmRange = (index) => {
        resetFeedback();
        setKmRanges((prev) => prev.filter((_, i) => i !== index));
        setIsDirty(true);
    };

    const onSave = async () => {
        if (disabled || !isDirty) {
            return;
        }
        const validRanges = kmRanges
            .filter((r) => Number(r.maxKm) > 0)
            .map((r) => ({maxKm: Number(r.maxKm), value: r.value ? r.value : 0}));
        // Valida faixas quando o modo é por faixa de quilometragem.
        if (calculationMode === MODE_KM_RANGE && validRanges.length === 0) {
            setErrorMessage('Informe ao menos uma faixa com quilometragem maior que zero.');
            return;
        }
        resetFeedback();
        setLoading(true);
        try {
            const payload = {
                calculationMode,
                fixedValue: fixedValue ? fixedValue : 0,
                perKmValue: perKmValue ? perKmValue : 0,
                kmRanges: validRanges,
            };
            await saveFreightConfiguration(payload);
            setSuccessMessage('Configuração de frete salva com sucesso.');
            setIsDirty(false);
        } catch (error) {
            console.error('Erro ao salvar configuração de frete:', error);
            setErrorMessage('Não foi possível salvar a configuração. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="company-closed-days-calendar">
            <div className="header-label">Frete</div>

            <div style={{display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '320px', margin: '10px auto'}}>
                <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                    <label style={{fontWeight: 600}}>Modo de cálculo do frete:</label>
                    <div style={{display: 'flex', flexDirection: 'row', gap: '16px'}}>
                        <label style={{display: 'flex', alignItems: 'center', gap: '6px', cursor: disabled ? 'default' : 'pointer'}}>
                            <input
                                type="radio"
                                name="freightCalculationMode"
                                checked={calculationMode === MODE_PER_KM}
                                onChange={() => handleModeChange(MODE_PER_KM)}
                                disabled={disabled}
                            />
                            Por km
                        </label>
                        <label style={{display: 'flex', alignItems: 'center', gap: '6px', cursor: disabled ? 'default' : 'pointer'}}>
                            <input
                                type="radio"
                                name="freightCalculationMode"
                                checked={calculationMode === MODE_KM_RANGE}
                                onChange={() => handleModeChange(MODE_KM_RANGE)}
                                disabled={disabled}
                            />
                            Por faixa de Km
                        </label>
                    </div>
                </div>

                {calculationMode === MODE_PER_KM && (
                    <>
                        <div style={{display: 'flex', flexDirection: 'column'}}>
                            <label>Valor de frete fixo (R$):</label>
                            <DecimalInput
                                style={{width: '160px'}}
                                placeholder="0,00"
                                value={fixedValue}
                                name="fixedValue"
                                onChange={handleFixedValueChange}
                                disabled={disabled}
                            />
                        </div>

                        <div style={{display: 'flex', flexDirection: 'column'}}>
                            <label>Valor de frete por km (R$):</label>
                            <DecimalInput
                                style={{width: '160px'}}
                                placeholder="0,00"
                                value={perKmValue}
                                name="perKmValue"
                                onChange={handlePerKmValueChange}
                                disabled={disabled}
                            />
                        </div>

                        <div style={{fontSize: '13px', color: '#555'}}>
                            O frete é calculado na tela de aluguéis como:
                            <br/>
                            <strong>valor fixo + valor por km × distância aproximada (km)</strong>.
                        </div>
                    </>
                )}

                {calculationMode === MODE_KM_RANGE && (
                    <>
                        <div style={{width: '180px'}}>Faixas de frete:</div>
                        <table style={{border: '1px solid', borderColor: 'lightgray', borderCollapse: 'collapse'}}>
                            <thead>
                            <tr>
                                <th style={{fontSize: '12px'}}>Até (km)</th>
                                <th style={{fontSize: '12px'}}>Valor (R$)</th>
                                <th/>
                            </tr>
                            </thead>
                            <tbody>
                            {kmRanges.map((item, index) => (
                                <tr key={index}>
                                    <td style={{padding: '2px'}}>
                                        <input
                                            type="number"
                                            step="1"
                                            placeholder="0"
                                            style={{width: '70px'}}
                                            value={item.maxKm}
                                            data-test={`freight-range-km-${index}`}
                                            onChange={(e) => handleKmRangeChange(index, 'maxKm',
                                                e.target.value === '' ? '' : parseFloat(e.target.value))}
                                            disabled={disabled}
                                        />
                                    </td>
                                    <td style={{padding: '2px'}}>
                                        <DecimalInput
                                            placeholder="0,00"
                                            style={{width: '80px'}}
                                            value={item.value}
                                            data-test={`freight-range-value-${index}`}
                                            onChange={(e) => handleKmRangeChange(index, 'value', e.target.value)}
                                            disabled={disabled}
                                        />
                                    </td>
                                    <td style={{padding: '2px'}}>
                                        {index < kmRanges.length - 1 && (
                                            <DeleteButton className="remove-button" onClick={() => removeKmRange(index)}/>)}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>

                        <div style={{fontSize: '13px', color: '#555'}}>
                            O frete na tela de aluguéis usa o <strong>valor da menor faixa cujo limite (km)
                            seja maior ou igual à distância aproximada</strong>. Acima da maior faixa, usa-se o valor dela.
                        </div>
                    </>
                )}
            </div>

            <div className="calendar-actions">
                <button onClick={loadConfiguration} disabled={disabled}>
                    Recarregar
                </button>
                <button onClick={onSave} disabled={disabled || !isDirty}>
                    Confirmar
                </button>
            </div>
            {successMessage && (
                <div className="calendar-status calendar-status-success">
                    {successMessage}
                </div>
            )}
            {errorMessage && (
                <div className="calendar-status calendar-status-error">
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

export default FreightConfiguration;
